import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Typography from '@mui/joy/Typography'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ActiveReservationBenefitCode } from '@/types/reservation-checkout'

export function getBenefitCodeOptionLabel(benefitCode: ActiveReservationBenefitCode) {
    return `${benefitCode.name} · ${benefitCode.code}`
}

export default function BenefitCodePayment({
    availableBenefitCodes,
    expanded,
    onExpandedChange,
    requestInProgress,
    errorMessage,
    onSubmit,
}: {
    availableBenefitCodes: ActiveReservationBenefitCode[]
    expanded: boolean
    onExpandedChange: (expanded: boolean) => void
    requestInProgress: boolean
    errorMessage: string | null
    onSubmit: (benefitCode: string) => Promise<void> | void
}) {
    const { t } = useTranslation()
    const benefitCodeSelectLabelId = useId()
    const [selectedCode, setSelectedCode] = useState<string | null>(null)
    const [validationError, setValidationError] = useState<string | null>(null)

    if (availableBenefitCodes.length === 0) {
        return null
    }

    const handleSubmit = async () => {
        if (!selectedCode) {
            setValidationError(t('checkout.benefitCodePayment.validation.required'))
            return
        }

        setValidationError(null)
        await onSubmit(selectedCode)
    }

    const handleCancel = () => {
        setValidationError(null)
        onExpandedChange(false)
    }

    if (!expanded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                    variant="plain"
                    onClick={() => {
                        setValidationError(null)
                        onExpandedChange(true)
                    }}
                    sx={{
                        color: 'black',
                        textDecoration: 'underline',
                    }}
                >
                    {t('checkout.benefitCodePayment.reveal')}
                </Button>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                alignItems: 'stretch',
                width: '100%',
            }}
        >
            <FormControl
                sx={{
                    width: '100%',
                    minWidth: 0,
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    <Typography
                        id={benefitCodeSelectLabelId}
                        component="label"
                        htmlFor="checkout-benefit-code-button"
                        sx={theme => ({
                            position: 'absolute',
                            lineHeight: 1,
                            top: '0.5rem',
                            left: '14px',
                            fontSize: '0.75rem',
                            color: theme.vars.palette.text.tertiary,
                            fontWeight: theme.vars.fontWeight.md,
                            pointerEvents: 'none',
                            zIndex: 1,
                        })}
                    >
                        {t('checkout.benefitCodePayment.label')}
                    </Typography>
                    <Select
                        value={selectedCode}
                        onChange={(_event, value) => {
                            setSelectedCode(value)
                            if (validationError) {
                                setValidationError(null)
                            }
                        }}
                        placeholder={t('checkout.benefitCodePayment.placeholder')}
                        slotProps={{
                            button: {
                                id: 'checkout-benefit-code-button',
                                'aria-labelledby': `${benefitCodeSelectLabelId} checkout-benefit-code-button`,
                                sx: {
                                    width: '100%',
                                    fontWeight: 'inherit',
                                    justifyContent: 'flex-start',
                                    minHeight: '56px',
                                    pt: '1rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    '&:focus': {
                                        boxShadow: 'none',
                                        outline: 'none',
                                    },
                                    '&:hover': {
                                        color: 'inherit',
                                    },
                                },
                            },
                            listbox: {
                                sx: {
                                    minWidth: 0,
                                    width: '100%',
                                    overflowX: 'hidden',
                                },
                            },
                        }}
                        sx={theme => ({
                            '--Select-minHeight': '56px',
                            '--Select-radius': '6px',
                            width: '100%',
                            minWidth: 0,
                            ...(validationError
                                ? {
                                      borderColor: theme.vars.palette.danger[500],
                                      '&:hover': {
                                          borderColor: theme.vars.palette.danger[500],
                                      },
                                      '&:focus-within': {
                                          borderColor: theme.vars.palette.danger[500],
                                      },
                                  }
                                : {}),
                        })}
                    >
                        {availableBenefitCodes.map(benefitCode => (
                            <Option
                                key={benefitCode.code}
                                value={benefitCode.code}
                                sx={{
                                    display: 'block',
                                    maxWidth: '100%',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {getBenefitCodeOptionLabel(benefitCode)}
                            </Option>
                        ))}
                    </Select>
                </Box>
            </FormControl>
            {validationError ? (
                <Typography level="body-xs" color="danger">
                    {validationError}
                </Typography>
            ) : null}
            <Box
                sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    variant="plain"
                    onClick={handleCancel}
                    disabled={requestInProgress}
                    sx={{
                        color: 'black',
                        borderRadius: '7px',
                    }}
                >
                    {t('checkout.benefitCodePayment.cancel')}
                </Button>
                {/* Required by CheckoutContactInfoForm's email blur/submission contract. */}
                <Button
                    data-checkout-submission
                    color="primary"
                    variant="solid"
                    size="sm"
                    onClick={() => {
                        void handleSubmit()
                    }}
                    loading={requestInProgress}
                    sx={{
                        fontSize: '15px',
                        padding: '0.875rem 1.25rem',
                        borderRadius: '7px',
                        backgroundColor: 'customBlack.main',
                    }}
                >
                    {t('checkout.benefitCodePayment.submit')}
                </Button>
            </Box>
            {errorMessage ? (
                <Typography level="body-xs" color="danger" textAlign="center">
                    {errorMessage}
                </Typography>
            ) : null}
        </Box>
    )
}
